import React from 'react';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UICircleChartWithLegend from '@/components/shared/UICircleChartWithLegend';
import { type IProperty, IUnit } from '@/types';
import { UICircleChartSegment } from '@/components/shared/UICircleChart';
import { IPropertyStatisticsData } from '@/api/statistics';
import './PropertyStatistics.scss';

export interface PropertyStatisticsProps {
  /**
   * Об'єкт нерухомості
   */
  property?: IProperty | null;

  /**
   * Статистичні дані для Property
   */
  statistics?: IPropertyStatisticsData; // Add new prop for statistics

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник кліку по згортанню
   */
  onToggleCollapse?: (collapsed: boolean) => void;
}

const baseClass = 'PropertyStatistics';

const PropertyStatistics: React.FC<PropertyStatisticsProps> = ({
  property,
  statistics, // Destructure the new prop
  loading = false,
  collapsed,
  className = '',
  onToggleCollapse,
}) => {

  // Дані для статистики Units (для діаграми - тільки значення > 0)
  const unitsChartSegments = React.useMemo((): UICircleChartSegment[] => {
    if (statistics?.units) {
      const { leased, pending, vacant } = statistics.units;
      const total = leased + pending + vacant; // Calculate total on frontend
      const segments: UICircleChartSegment[] = [];

      if (leased > 0) {
        segments.push({ label: 'Leased', value: leased, percentage: total > 0 ? Math.round((leased / total) * 100) : 0, color: '#5CB2EF' });
      }
      if (pending > 0) {
        segments.push({ label: 'Pending', value: pending, percentage: total > 0 ? Math.round((pending / total) * 100) : 0, color: '#FF9C07' });
      }
      if (vacant > 0) {
        segments.push({ label: 'Vacant', value: vacant, percentage: total > 0 ? Math.round((vacant / total) * 100) : 0, color: '#105E96' });
      }
      return segments;
    }

    // Fallback to existing logic if statistics are not available
    if (!property) {
      return [
        { label: 'Leased', value: 15, percentage: 65, color: '#5CB2EF' },
        { label: 'Vacant', value: 8, percentage: 35, color: '#105E96' },
      ];
    }

    const totalUnits = property.units?.length || 23;
    const leasedUnits = property.units?.filter((unit: IUnit) => unit.tenant)?.length || 15;
    const vacantUnits = totalUnits - leasedUnits;

    const leasedPercentage = totalUnits > 0 ? Math.round((leasedUnits / totalUnits) * 100) : 0;
    const vacantPercentage = 100 - leasedPercentage;

    return [
      { label: 'Leased', value: leasedUnits, percentage: leasedPercentage, color: '#5CB2EF' },
      { label: 'Vacant', value: vacantUnits, percentage: vacantPercentage, color: '#105E96' },
    ];
  }, [statistics, property]);

  // Дані для статистики Payment
  const paymentChartSegments = React.useMemo((): UICircleChartSegment[] => {
    if (statistics?.payments) {
      const { paid, arrears, notSet } = statistics.payments;

      return [
        { label: 'Paid', value: paid, percentage: paid, color: '#76C102' },
        { label: 'Arrears', value: arrears, percentage: arrears, color: '#FB2F54' },
        { label: 'Not set', value: notSet, percentage: notSet, color: '#FF9C07' },
      ];
    }

    // Fallback to mock data if statistics are not available
    return [
      { label: 'Paid', value: 64, percentage: 64, color: '#76C102' },
      { label: 'Arrears', value: 22, percentage: 22, color: '#FB2F54' },
      { label: 'Not set', value: 14, percentage: 14, color: '#FF9C07' },
    ];
  }, [statistics]);

  // Дані для легенди Units (всі категорії, навіть якщо 0)
  const unitsLegendData = React.useMemo(() => {
    let total = 0;
    let leased = 0;
    let pending = 0;
    let vacant = 0;

    if (statistics?.units) {
      leased = statistics.units.leased;
      pending = statistics.units.pending;
      vacant = statistics.units.vacant;
      total = leased + pending + vacant; // Calculate total on frontend
    } else if (property) {
      // Fallback using property data
      total = property.units?.length || 0;
      leased = property.units?.filter((unit: IUnit) => unit.tenant)?.length || 0;
      vacant = total - leased;
      pending = 0; // Assuming no pending in old property data
    } else {
      // Fallback to mock data if neither statistics nor property is available
      total = 23;
      leased = 15;
      vacant = 8;
      pending = 0;
    }

    const leasedPercentage = total > 0 ? Math.round((leased / total) * 100) : 0;
    const pendingPercentage = total > 0 ? Math.round((pending / total) * 100) : 0;
    const vacantPercentage = total > 0 ? Math.round((vacant / total) * 100) : 0;

    return [
      { label: 'Units', value: total, percentage: null, color: 'transparent' },
      { label: 'Leased', value: leased, percentage: leasedPercentage, color: '#5CB2EF' },
      { label: 'Pending', value: pending, percentage: pendingPercentage, color: '#FF9C07' },
      { label: 'Vacant', value: vacant, percentage: vacantPercentage, color: '#105E96' },
    ];
  }, [statistics, property]);

  const paymentLegendData = paymentChartSegments; // Легенда платежів може використовувати ті ж сегменти, що й діаграма

  const renderStatistics = () => (
    <div className={`${baseClass}__stats`}>
      {/* Units Statistics */}
      <div className={`${baseClass}__stat-group`}>
        <div className={`${baseClass}__stat-legend`}>
          {unitsLegendData.map((stat, index) => (
            <div key={index} className={`${baseClass}__legend-item`}>
              {stat.color !== 'transparent' && (
                <div
                  className={`${baseClass}__legend-dot`}
                  style={{ backgroundColor: stat.color }}
                />
              )}
              <span className={`${baseClass}__legend-label`}>
                {stat.label}
              </span>
              <span className={`${baseClass}__legend-value`}>
                {stat.value}
              </span>
            </div>
          ))}
        </div>
        <div className={`${baseClass}__stat-chart`}>
          <UICircleChartWithLegend
            segments={unitsChartSegments}
            size={104}
            strokeWidth={16}
            backgroundColor="#F4F6F8"
            legendPosition="left"
            gap={0}
            className={`${baseClass}__chart-component`}
          />
        </div>
      </div>

      {/* Payment Statistics */}
      <div className={`${baseClass}__stat-group`}>
        <div className={`${baseClass}__stat-legend`}>
          {paymentLegendData.map((stat, index) => (
            <div key={index} className={`${baseClass}__legend-item`}>
              <div
                className={`${baseClass}__legend-dot`}
                style={{ backgroundColor: stat.color }}
              />
              <span className={`${baseClass}__legend-label`}>
                {stat.label}
              </span>
              <span className={`${baseClass}__legend-value`}>
                {stat.percentage}%
              </span>
            </div>
          ))}
        </div>
        <div className={`${baseClass}__stat-chart`}>
          <UICircleChartWithLegend
            segments={paymentChartSegments}
            size={104}
            strokeWidth={16}
            backgroundColor="#F4F6F8"
            legendPosition="left"
            gap={0}
            className={`${baseClass}__chart-component`}
          />
        </div>
      </div>
    </div>
  );

  return (
    <UICollapsibleSection
      title="Property statistics"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading statistics..."
      className={`${baseClass} ${className}`.trim()}
    >
      {renderStatistics()}
    </UICollapsibleSection>
  );
};

export default PropertyStatistics;
